import { Link } from "react-router-dom";
import { ArrowRight, Check, Stethoscope } from "lucide-react";
import { PageIntro } from "@/components/kit/PageIntro";
import { CtaBand } from "@/components/kit/CtaBand";
import { Reveal } from "@/components/site/Motion";
import { JsonLd, serviceSchema, usePageMeta } from "@/components/site/Seo";
import { Audience, PROGRAMME_PATH, SITE } from "@/lib/content";

function ListCard({ title, items, testId }: { title: string; items: string[]; testId: string }) {
  return (
    <div className="h-full rounded-[28px] bg-beige-soft p-8 ring-1 ring-forest/10 sm:p-10" data-testid={testId}>
      <h2 className="font-display text-4xl text-forest">{title}</h2>
      <ul className="mt-6 space-y-4">{items.map((x) => <li key={x} className="flex gap-3 text-base text-charcoal"><Check className="mt-1 h-4 w-4 shrink-0 text-pine" />{x}</li>)}</ul>
    </div>
  );
}

export default function AudiencePage({ audience: a }: { audience: Audience }) {
  usePageMeta(a.title, `${a.short} With wellness coach Sony Mathew. Free 20-minute consultation.`);
  const [first, ...rest] = a.title.split(" ");
  const aside = (
    <div className="arch relative mx-auto aspect-[4/5] max-w-[420px] overflow-hidden">
      <img src={a.image} alt={a.alt} className="h-full w-full scale-105 object-cover" />
    </div>
  );
  return (
    <div data-testid={`audience-page-${a.slug}`}>
      <JsonLd id="service" data={serviceSchema(a.title, a.short, `/${a.slug}`)} />
      <PageIntro testId="audience-hero" eyebrow={`${a.pillar} · Part of the 120-day programme`} lines={[first, <em key="r" className="text-gold-soft">{rest.join(" ")}</em>]} intro={a.intro} aside={aside} />
      <section className="bg-beige py-20 sm:py-28">
        <div className="wrap">
          {a.medical && (
            <Reveal className="mb-6">
              <div className="flex gap-4 rounded-[24px] border border-gold/50 bg-beige-soft p-6 sm:p-8" data-testid="medical-note">
                <Stethoscope className="mt-1 h-5 w-5 shrink-0 text-gold-ink" />
                <p className="text-base leading-relaxed text-charcoal"><strong className="font-semibold text-forest">Nutrition support alongside your doctor's treatment.</strong> {SITE.founder} is a wellness coach, not a dietitian. We don't diagnose, prescribe or change medication. Please keep your doctor involved.</p>
              </div>
            </Reveal>
          )}
          <div className="grid gap-5 md:grid-cols-2">
            <Reveal><ListCard title="This is for you if" items={a.forYou} testId="audience-for-you" /></Reveal>
            <Reveal delay={0.1}><ListCard title="How we support you" items={a.support} testId="audience-support" /></Reveal>
          </div>
          <Reveal className="mt-6">
            <Link to={PROGRAMME_PATH} data-testid="audience-programme-link" className="grain group relative flex flex-col justify-between gap-6 overflow-hidden rounded-[28px] bg-forest p-8 text-cream sm:flex-row sm:items-center sm:p-10">
              <span className="relative"><span className="eyebrow-dark">Delivered through</span><span className="mt-3 block font-display text-3xl sm:text-4xl">The 120-Day Wellness Journey</span></span>
              <span className="relative inline-flex shrink-0 items-center gap-2 font-display text-3xl text-gold-soft">{SITE.price}/mo <ArrowRight className="h-6 w-6 transition-transform duration-500 group-hover:translate-x-2" /></span>
            </Link>
          </Reveal>
        </div>
      </section>
      <CtaBand testId="audience-cta" />
    </div>
  );
}
